import { useCallback, useEffect, useState } from "react";
import { fmtMYTDate } from "@/lib/myt";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function MemberDetailModal({ memberId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [progressForm, setProgressForm] = useState({ add: "", set: "" });
  const [progressBusy, setProgressBusy] = useState(false);
  const [opIds, setOpIds] = useState({ add: "", set: "" });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/members/${memberId}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      onClose();
    }
  }, [memberId, onClose]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  const submitProgress = async (mode) => {
    const raw = progressForm[mode];
    if (raw === "" || Number.isNaN(Number(raw))) {
      toast.error("Enter a numeric progress value");
      return;
    }
    // One durable operation id per logical action: reused if the admin retries a failed
    // submit (server dedupes), regenerated after a success or a value change (new action).
    let opId = opIds[mode];
    if (!opId) {
      opId = crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      setOpIds((p) => ({ ...p, [mode]: opId }));
    }
    setProgressBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${memberId}/progress`, {
        mode,
        value: Number(raw),
        operation_id: opId,
      });
      toast.success(
        data.duplicate_ignored
          ? "Duplicate ignored — already added"
          : data.promoted_to
            ? `Promoted to ${data.promoted_to} — rank updated automatically`
            : "Progress updated",
      );
      setProgressForm({ add: "", set: "" });
      setOpIds((p) => ({ ...p, [mode]: "" }));
      await load();
      onChanged?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgressBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="view-member-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="card-lux relative w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto">
        {!detail ? (
          <p className="text-sm text-[#64748B]">Loading member profile...</p>
        ) : (
          <>
            <p className="eyebrow-label mb-2">Member Profile</p>
            <h3 className="font-heading text-lg font-semibold text-white" data-testid="view-member-name">{detail.full_name}</h3>
            <p className="text-xs text-[#64748B] font-mono mb-6">{detail.uid} · {detail.username} · {detail.email}</p>
            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              <div><p className="text-xs text-[#64748B]">Rank</p><p className="text-[#D4AF37] font-medium">{detail.rank?.name}{detail.rank?.level ? ` (Lv.${detail.rank.level})` : ""}</p></div>
              <div><p className="text-xs text-[#64748B]">Next Rank</p><p className="text-white">{detail.next_rank_name || "Max rank"}</p></div>
              <div><p className="text-xs text-[#64748B]">Phone</p><p className="text-white">{detail.phone || "—"}</p></div>
              <div><p className="text-xs text-[#64748B]">Country</p><p className="text-white">{detail.country || "—"}</p></div>
              <div><p className="text-xs text-[#64748B]">Sponsor IB</p><p className="text-white">{detail.sponsor_name || "—"} <span className="font-mono text-[#D4AF37] text-xs">{detail.sponsor_code || ""}</span></p></div>
              <div><p className="text-xs text-[#64748B]">Registered Broker</p><p className="text-white font-mono" data-testid="view-member-broker">{detail.registered_broker || "Not Provided"}</p></div>
              <div><p className="text-xs text-[#64748B]">Status</p><p className={detail.status === "active" ? "text-emerald-400" : "text-red-400"}>{detail.status}</p></div>
              <div><p className="text-xs text-[#64748B]">Joined</p><p className="text-white">{detail.created_at ? fmtMYTDate(detail.created_at) : "—"}</p></div>
              <div><p className="text-xs text-[#64748B]">Redemptions</p><p className="font-mono text-white">{detail.redemption_count}</p></div>
            </div>
            <p className="eyebrow-label mb-3">Private Progress Management</p>
            <div className="border border-[#D4AF37]/25 bg-[#0B0C10]/60 rounded-xl p-4 mb-6 space-y-4" data-testid="private-progress-section">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-[#64748B]">Current Rank</p><p className="text-[#D4AF37] font-medium" data-testid="progress-current-rank">{detail.rank?.name}</p></div>
                <div><p className="text-[#64748B]">Next Rank</p><p className="text-white" data-testid="progress-next-rank">{detail.progression?.next?.name || "Max rank achieved"}</p></div>
                <div><p className="text-[#64748B]">Total Progress</p><p className="font-mono text-white" data-testid="progress-value">{detail.progression?.private?.progress ?? 0}</p></div>
                <div><p className="text-[#64748B]">Next-Rank Requirement</p><p className="font-mono text-white" data-testid="progress-requirement">{detail.progression?.private?.requirement ?? "—"}</p></div>
                <div className="col-span-2"><p className="text-[#64748B]">Auto-calculated Progress</p><p className="font-mono text-[#D4AF37]" data-testid="progress-percentage">{detail.progression?.progress_pct ?? 0}%</p></div>
              </div>
              <div>
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#94A3B8] mb-2">Normal Action</p>
                <div className="flex gap-2">
                  <input type="number" step="any" min="0" className="input-lux flex-1 font-mono text-sm" placeholder="Enter Progress Amount" value={progressForm.add} onChange={(e) => { setProgressForm((f) => ({ ...f, add: e.target.value })); setOpIds((p) => ({ ...p, add: "" })); }} data-testid="progress-add-input" />
                  <button onClick={() => submitProgress("add")} disabled={progressBusy} className="btn-gold px-4 py-2 text-xs shrink-0 disabled:opacity-60" data-testid="progress-add-button">Add Progress</button>
                </div>
              </div>
              <div className="border-t border-[#222530] pt-3">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">Advanced Correction</p>
                <div className="flex gap-2">
                  <input type="number" step="any" min="0" className="input-lux flex-1 font-mono text-sm" placeholder="Set Exact Total Value" value={progressForm.set} onChange={(e) => { setProgressForm((f) => ({ ...f, set: e.target.value })); setOpIds((p) => ({ ...p, set: "" })); }} data-testid="progress-set-input" />
                  <button onClick={() => submitProgress("set")} disabled={progressBusy} className="btn-outline-gold px-4 py-2 text-xs shrink-0 disabled:opacity-60" data-testid="progress-set-button">Save Exact Value</button>
                </div>
              </div>
              <p className="text-[10px] text-[#64748B]">Admin-only. Progress accumulates automatically — members see percentage only, never raw values.</p>
            </div>
            <p className="eyebrow-label mb-3">Delivery Address</p>
            {detail.address ? (
              <div className="text-xs text-[#94A3B8] border border-[#222530] rounded-lg px-4 py-3 leading-relaxed" data-testid="view-member-address">
                <p className="text-white font-medium">{detail.address.recipient_name}</p>
                <p>{detail.address.address_line1}{detail.address.address_line2 ? `, ${detail.address.address_line2}` : ""}</p>
                <p>{detail.address.city}, {detail.address.state} {detail.address.postal_code}</p>
                <p>{detail.address.country} · {detail.address.phone}</p>
              </div>
            ) : (
              <p className="text-xs text-[#64748B]">No delivery address on file.</p>
            )}
            <p className="eyebrow-label mb-3">Indicator Access</p>
            {detail.indicator_assignments?.length ? (
              <div className="space-y-2" data-testid="view-member-indicators">
                {detail.indicator_assignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm border border-[#222530] rounded-lg px-4 py-2.5">
                    <span className="text-white">{a.indicator_name}</span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${a.status === "active" ? "text-emerald-400" : "text-red-400"}`}>{a.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#64748B]">No indicator access granted.</p>
            )}
            <p className="eyebrow-label mb-3 mt-6">Redemption History</p>
            {detail.redemption_history?.length ? (
              <div className="space-y-2" data-testid="view-member-redemptions">
                {detail.redemption_history.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 text-sm border border-[#222530] rounded-lg px-4 py-2.5">
                    <div>
                      <span className="text-white">{r.reward_name}</span>
                      <span className="block text-[10px] text-[#64748B]">{r.created_at ? fmtMYTDate(r.created_at) : ""}</span>
                    </div>
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${r.status === "rejected" ? "text-red-400" : r.status === "pending" ? "text-[#D4AF37]" : "text-emerald-400"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#64748B]">No redemption requests.</p>
            )}
            <button onClick={onClose} className="btn-outline-gold w-full py-2.5 text-sm mt-6" data-testid="view-member-close-button">Close</button>
          </>
        )}
      </div>
    </div>
  );
}
